import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { PRESETS, PRESET_BY_NAME } from '../presets/index.js';

// Shared-rig contract: forbidden - the light is the buffer's own history,
// as in Echo.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js). requiresTier 1:
// the ping-pong pass pair plus the wave and the shapes do not fit tier 0's
// budget; the tier-0 layout below exists only as the governor's refuge.
export const PROFILE = {
  id: 'preset',
  family: 'shader',
  danceability: 0.85,
  requiresTier: 1,
  energyLevel: 0.82,
  bpmMin: 100,
  bpmMax: 180,
  movement: 0.85,
  tonality: 0.55,
  prefers: { gridStrength: [0.55, 1.0] },
  enabled: true
};

// Preset: a MilkDrop-style preset ENGINE. One feedback buffer, deformed per
// pixel by a field of equations whose coefficients are a preset - a small
// JSON object (see ../presets/) - with synthetic waves injected into it
// every frame and the album cover on one to three polygons. Echo's zoom,
// Advect's warp and a kaleidoscope are each one corner of this parameter
// space.
//
// THE DECISIVE DETAIL: a transition between two presets is NOT a crossfade
// of two renders. It is an interpolation of their PARAMETERS over 2.5 s,
// so halfway the buffer is deformed by a field that belongs to neither -
// the image passes through hybrid forms, which is what MilkDrop's
// transitions are known for. A CUT changes the parameters in one frame
// and keeps the buffer: the previous image is caught in the new field.
//
// STABILITY is Echo's, verbatim: decay < 1, a floor subtraction, a hard
// ceiling, a luminance governor on an 8x8 readback, a signal-masked dither.
// Two things a parameter space adds: the partial inversion is applied on
// DISPLAY, never inside the loop (there it has a grey fixed point - see the
// display shader), and the per-pixel zoom is bounded away from r = 0
// (pow(r, e - 1) with e < 1 explodes at the centre).
//
// THE RENDER PASSES run inside the display quad's onBeforeRender - see
// echo.js's header for why that is the one legal place.

// --- the loop (Echo's guards) ---------------------------------------------------
const FLOOR_SUB = 0.004;
const CEILING = 0.92;
const DECAY_MIN = 0.85;
const DECAY_MAX = 0.975;
const GOVERN_EVERY_FRAMES = 30;
const GOVERN_HIGH = 0.35;
const GOVERN_RESUME = 0.25;
const GOVERN_LOW = 0.05;
const GOVERN_STEP = 0.01;
const LUM_SIZE = 8;
const FADE_DECAY = 0.96;       // track change: Echo's 1.2 s fade to black
const TRACK_FADE_S = 1.2;

// --- transitions --------------------------------------------------------------------
const FADE_S = 2.5;
const CUT_EVERY_PHRASES = 4;   // one phrase in four is a cut, the others fade
const HISTORY = 4;             // presets not re-picked for this many picks

// --- the rhythm inside a preset ----------------------------------------------------
const BEAT_ZOOM = 0.012;       // x bands.low, for BEAT_HOLD_S
const BEAT_HOLD_S = 0.12;
const BAR_ROT = 0.012;         // radians per frame impulse, decaying
const BAR_ROT_TAU = 0.25;
const BAR_HUE_STEP = 1 / 12;   // a notch of the wheel
const DROP_INVERT_FRAMES = 2;
const BAND_TAU = 0.15;

// --- the waves -----------------------------------------------------------------------
const WAVE_POINTS = 256;
const WAVE_PX = 3.0;           // core width in buffer pixels (2-4)
const WAVE_HALO = 2.2;         // halo over the core
const WAVE_GAIN = 0.42;        // re-injected every frame and stacked by the loop: 0.85 whited the frame
const WAVE_AMP = 0.10;         // of the buffer height, at full band level
const BAND_CENTRE = [0.18, 0.5, 0.82];   // low, mid, high along the curve
const BAND_SIGMA = [0.16, 0.14, 0.12];
const DECORRELATION = 0.10;
const ATTACK_TAU = 0.03, RELEASE_TAU = 0.18;   // EqRig's ballistics
const TEMPO_WOBBLE = 0.35;     // of the amplitude, at the tempo
const DOT_PERIOD = 0.0625;     // DOTS: one dot per 16th of the curve

// --- the shapes ----------------------------------------------------------------------
const SHAPE_OPACITY = 0.45;
const SHAPE_SIZE = 0.26;       // of the buffer height
const SHAPE_PULSE = 0.12;

const SAFE_FLOOR = 0.50;

const TIER = {
  // Tier 0 only as a live-downgrade refuge (see echo.js): the orchestrator
  // never offers Preset below tier 1.
  0: { scale: 0.25, warp: false, shapes: false, count: 10 },
  1: { scale: 0.25, warp: false, shapes: false, count: 10 },
  2: { scale: 0.5, warp: true, shapes: true, count: 16 }
};
const DEFAULT_TIER = 2;

// The interpolated parameters, in the order the shader takes them.
const PARAMS = ['cx', 'cy', 'zoom', 'zoomexp', 'rot', 'rotr', 'dx', 'dy', 'sx', 'sy',
  'warp', 'warpScale', 'warpSpeed', 'decay', 'invert', 'hueShift', 'darkenCenter'];
// The angular ones take the shortest way round: the wave's orientation
// (a preset may set `waveAngle`, in turns, 0 by default) and the shapes'
// spin phase.
const ANGULAR = { waveAngle: 1.0 };
const WAVE_TYPES = ['LINE', 'CIRCLE', 'SPIRO', 'DOTS', 'MIRROR'];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function smooth(t) { return t * t * (3 - 2 * t); }
function lerpAngle(a, b, t, period) {
  let d = (b - a) % period;
  if (d > period / 2) d -= period;
  if (d < -period / 2) d += period;
  return a + d * t;
}
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function hueMatrix(angle, out) {
  const c = Math.cos(angle), s = Math.sin(angle);
  const a = 1 / 3, b = Math.sqrt(1 / 3);
  out.set(
    c + (1 - c) * a, a * (1 - c) - b * s, a * (1 - c) + b * s,
    a * (1 - c) + b * s, c + (1 - c) * a, a * (1 - c) - b * s,
    a * (1 - c) - b * s, a * (1 - c) + b * s, c + (1 - c) * a
  );
  return out;
}

// =============================================================================
// SHADERS
// =============================================================================

const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// THE FIELD. Polar around (cx, cy) in aspect-corrected units (y spans 1):
//   zoom(r) = zoom * pow(2r, zoomexp - 1)     rot(r) = rot + rotr * r
//   p' = rotate(p, rot(r)) / zoom(r) + (dx, dy) + (s - 1) * p + warp * noise
// then the colour: decay, masked inversion, darkened centre, hue rotation,
// Echo's guards and dither.
const feedbackFragmentShader = `
  precision highp float;
  uniform sampler2D uPrevious;
  uniform float uAspect;
  uniform vec2 uCentre;
  uniform float uZoom, uZoomExp, uRot, uRotR;
  uniform vec2 uDelta;
  uniform vec2 uStretch;
  uniform float uWarp, uWarpScale, uWarpSpeed;
  uniform float uDecay, uDarken;
  uniform mat3 uHueMat;
  uniform float uTime;
  uniform float uSeed;
  varying vec2 vUv;

  // a smooth field of a few sines: cheap, fp16-safe (bounded arguments)
  vec2 warpField(vec2 p, float t) {
    float s = uWarpScale * 6.2832;
    return vec2(
      sin(p.y * s + t) * 0.6 + sin(p.x * s * 0.7 - t * 1.3 + p.y * s * 0.4) * 0.4,
      cos(p.x * s - t * 0.9) * 0.6 + cos(p.y * s * 0.8 + t * 1.1 - p.x * s * 0.5) * 0.4);
  }

  void main() {
    vec2 p = (vUv - uCentre) * vec2(uAspect, 1.0);
    float r = max(length(p), 0.02);
    float ang = uRot + uRotR * r;
    float c = cos(ang), s = sin(ang);
    p = vec2(p.x * c - p.y * s, p.x * s + p.y * c);
    // r normalised to the mid radius: zoomexp biases the zoom between
    // centre and rim without moving its value at r = 0.5. Raw pow(r, e - 1)
    // with e = 1.6 gave z = 0.4 at r = 0.22: the whole image collapsed to
    // the centre in a few frames (breathing-star soaked to black).
    float z = uZoom * pow(r * 2.0, uZoomExp - 1.0);
    p /= clamp(z, 0.5, 2.0);
    p += uDelta;
    p += (uStretch - 1.0) * p;
    p += uWarp * warpField(p, mod(uTime * uWarpSpeed, 6.2832));
    vec2 uv = p / vec2(uAspect, 1.0) + uCentre;
    vec3 prev = texture2D(uPrevious, uv).rgb;

    prev *= 1.0 - uDarken * smoothstep(0.25, 0.0, r);
    prev = uHueMat * prev;

    // STABILITY (echo.js): gain < 1, floor, ceiling
    prev *= uDecay;
    prev -= vec3(${FLOOR_SUB.toFixed(4)});
    prev = max(prev, 0.0);
    prev = min(prev, vec3(${CEILING.toFixed(3)}));

    vec2 fcw = mod(floor(gl_FragCoord.xy) + uSeed, 64.0);
    float dith = (fract(dot(fcw, vec2(0.7548776662, 0.5698402909))) - 0.5) / 255.0;
    float on = step(3.5 / 255.0, max(prev.r, max(prev.g, prev.b)));
    gl_FragColor = vec4(prev + dith * on, 1.0);
  }
`;

// THE WAVE: a ribbon of WAVE_POINTS samples, two vertices per sample,
// widened along the segment normal in ortho units (the buffer's height is
// 1). White core, coloured halo; DOTS cuts it into beads along the curve.
const waveVertexShader = `
  precision highp float;
  attribute float aSide;
  attribute float aAlong;
  uniform float uHalf;         // half width, ortho units
  varying float vSide;
  varying float vAlong;
  void main() {
    // position.xy = the sample, position.z = packed normal angle
    vec2 n = vec2(cos(position.z), sin(position.z));
    vec2 p = position.xy + n * uHalf * aSide;
    vSide = aSide;
    vAlong = aAlong;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 0.0, 1.0);
  }
`;
const waveFragmentShader = `
  precision mediump float;
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform float uDots;         // 1: beads
  varying float vSide;
  varying float vAlong;
  void main() {
    float r = abs(vSide);
    float core = 1.0 - smoothstep(0.0, 1.0 / ${WAVE_HALO.toFixed(1)}, r);
    float halo = (1.0 - r) * (1.0 - r);
    float bead = mix(1.0, 1.0 - smoothstep(0.30, 0.42, abs(fract(vAlong / ${DOT_PERIOD.toFixed(4)}) - 0.5)), uDots);
    vec3 col = (vec3(1.0) * core * 0.7 + uColor * halo * 1.0) * uOpacity * bead;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// The inversion lives HERE, on the display, not in the loop: inside the
// loop mix(prev, 1 - prev, i) has a fixed point near 0.37 for every pixel
// above the signal mask, and the trails leave a grey veil that never dies
// (measured on invert-strobe: black fraction 0). On display it is a
// negative of the trails, masked on signal so black stays black; the drop's
// two-frame flash (uFlash) is the unmasked full negative.
const displayFragmentShader = `
  precision mediump float;
  uniform sampler2D uBuffer;
  uniform float uInvert, uFlash;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  void main() {
    vec2 ndc = vUv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float att = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
    vec3 c = texture2D(uBuffer, vUv).rgb;
    float sig = smoothstep(0.05, 0.25, max(c.r, max(c.g, c.b)));
    c = mix(c, 1.0 - c, uInvert * sig);
    c = mix(c, 1.0 - c, uFlash);
    gl_FragColor = vec4(c * att, 1.0);
  }
`;

const lumFragmentShader = `
  precision mediump float;
  uniform sampler2D uBuffer;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uBuffer, vUv).rgb, 1.0); }
`;

// =============================================================================

export class PresetVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    // the library this tier may run: without warp, the warp-free presets
    this._library = PRESETS.filter((p) => this._layout.warp || !p.warp).slice(0, this._layout.count);
    const forced = urlParam('preset');
    this._forced = forced && PRESET_BY_NAME[forced] ? PRESET_BY_NAME[forced] : null;
    const blend = urlParam('presetblend');
    this._blend = null;
    if (blend) {
      const parts = blend.split(',');
      if (parts.length === 3 && PRESET_BY_NAME[parts[0]] && PRESET_BY_NAME[parts[1]] && Number.isFinite(parseFloat(parts[2]))) {
        this._blend = { a: PRESET_BY_NAME[parts[0]], b: PRESET_BY_NAME[parts[1]], t: clamp01(parseFloat(parts[2])) };
      }
    }
    this._listDebug = urlParam('presetlist') === '1';
    const forcedDecay = parseFloat(urlParam('presetdecay'));
    this._forcedDecay = Number.isFinite(forcedDecay) ? forcedDecay : null;
  }

  init(scene, camera) {
    this._scene = scene;
    this._buffer = new FeedbackBuffer({ scale: this._layout.scale });

    // --- feedback pass ---
    this._fb = {
      uPrevious: { value: null }, uAspect: { value: 16 / 9 }, uCentre: { value: new THREE.Vector2(0.5, 0.5) },
      uZoom: { value: 1 }, uZoomExp: { value: 1 }, uRot: { value: 0 }, uRotR: { value: 0 },
      uDelta: { value: new THREE.Vector2() }, uStretch: { value: new THREE.Vector2(1, 1) },
      uWarp: { value: 0 }, uWarpScale: { value: 4 }, uWarpSpeed: { value: 0.3 },
      uDecay: { value: 0.96 }, uDarken: { value: 0 },
      uHueMat: { value: hueMatrix(0, new THREE.Matrix3()) }, uTime: { value: 0 }, uSeed: { value: 0 }
    };
    this._feedbackScene = new THREE.Scene();
    this._feedbackQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._fb, vertexShader: quadVertexShader, fragmentShader: feedbackFragmentShader, depthTest: false, depthWrite: false
    }));
    this._feedbackQuad.frustumCulled = false;
    this._feedbackScene.add(this._feedbackQuad);

    // --- injection: the waves (two, for the crossfade) and the shapes ---
    this._injectScene = new THREE.Scene();
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    this._waves = [this._makeWave(), this._makeWave()];
    for (const w of this._waves) this._injectScene.add(w.mesh);
    this._shapes = [];
    this._artTexture = null;
    for (let i = 0; i < 3; i++) {
      const sides = 3 + i;   // triangle, square, pentagon
      const mesh = new THREE.Mesh(new THREE.CircleGeometry(0.5, sides),
        new THREE.MeshBasicMaterial({ transparent: true, opacity: SHAPE_OPACITY, depthTest: false, depthWrite: false }));
      mesh.visible = false;
      mesh.frustumCulled = false;
      this._injectScene.add(mesh);
      this._shapes.push(mesh);
    }

    // --- governor ---
    this._lumTarget = new THREE.WebGLRenderTarget(LUM_SIZE, LUM_SIZE, { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false });
    this._lumScene = new THREE.Scene();
    this._lumUniforms = { uBuffer: { value: null } };
    this._lumQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._lumUniforms, vertexShader: quadVertexShader, fragmentShader: lumFragmentShader, depthTest: false, depthWrite: false }));
    this._lumQuad.frustumCulled = false;
    this._lumScene.add(this._lumQuad);
    this._lumPixels = new Uint8Array(LUM_SIZE * LUM_SIZE * 4);

    // --- display quad in the main scene ---
    this._displayUniforms = Object.assign({ uBuffer: { value: null }, uInvert: { value: 0 }, uFlash: { value: 0 } }, safeZoneUniforms);
    this._displayMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._displayUniforms, vertexShader: quadVertexShader, fragmentShader: displayFragmentShader, depthTest: false, depthWrite: false }));
    this._displayMesh.frustumCulled = false;
    this._displayMesh.renderOrder = -1000;
    const self = this;
    this._displayMesh.onBeforeRender = function (renderer) { self._runPasses(renderer); };
    scene.add(this._displayMesh);

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._frameCount = 0;
    this._lastLum = 0;
    this._governorAdj = 0;       // subtracted from the preset's decay by the governor
    this._governorHot = false;
    this._lowSmooth = 0.5; this._midSmooth = 0.5; this._highSmooth = 0.5;
    this._bands = [0.5, 0.5, 0.5];
    this._bpm = null;
    this._palette = [new THREE.Color(0x39a0ff), new THREE.Color(0xff3ba7), new THREE.Color(0xffd23b)];
    this._lastPaletteRef = undefined;
    this._lastTrackId = undefined;
    this._lastArtworkUrl = undefined;
    this._fadeUntil = -1; this._fading = false;
    this._lastBeatIndex = null; this._lastBarIndex = null; this._lastPhraseIndex = null;
    this._beatUntil = -1; this._barRot = 0; this._hueOffset = 0; this._hueAngle = 0;
    this._invertFrames = 0;
    this._phraseCount = 0;
    this._firedDrops = new Set();
    this._history = [];
    this._energy = 0.6;
    // the wave's per-point state: decorrelation, ballistic level
    this._decor = new Float32Array(WAVE_POINTS);
    this._level = new Float32Array(WAVE_POINTS);
    let seed = 7;
    const rnd = () => { seed = (seed * 1103515245 + 12345) % 2147483648; return seed / 2147483648; };
    for (let i = 0; i < WAVE_POINTS; i++) this._decor[i] = 1 + (rnd() * 2 - 1) * DECORRELATION;
    this._gauss = [0, 1, 2].map((b) => { const g = new Float32Array(WAVE_POINTS); for (let i = 0; i < WAVE_POINTS; i++) { const d = (i / (WAVE_POINTS - 1) - BAND_CENTRE[b]) / BAND_SIGMA[b]; g[i] = Math.exp(-0.5 * d * d); } return g; });
    this._spiroPhase = 0;

    // the presets: current (from), target (to), and the blend t
    const first = this._blend ? this._blend.a : (this._forced || this._pick(null));
    this._from = this._params(first);
    this._to = this._params(this._blend ? this._blend.b : first);
    this._fromPreset = first;
    this._toPreset = this._blend ? this._blend.b : first;
    this._t = this._blend ? this._blend.t : 1;
    this._cur = Object.assign({}, this._from);
    this._cutLast = false;
    this._waveAngle = 0;

    if (this._listDebug) {
      const el = document.createElement('div');
      el.style.cssText = 'position:fixed;left:10px;top:10px;z-index:99999;font:12px/1.5 ui-monospace,monospace;color:#9fe;background:rgba(0,0,0,.65);padding:6px 9px;border-radius:6px;white-space:pre';
      document.body.appendChild(el);
      this._listEl = el;
    }
  }

  // --- presets ------------------------------------------------------------------------
  _params(p) {
    const o = {};
    for (const k of PARAMS) o[k] = p[k] != null ? p[k] : 0;
    o.waveAngle = p.waveAngle || 0;
    o.waveType = p.wave || 'LINE';
    o.waveColor = p.waveColor || 0;
    o.shapes = this._layout.shapes ? (p.shapes || 0) : 0;
    o.name = p.name;
    return o;
  }

  // The next preset: among those whose energy range holds the segment's
  // energy, not the current one, not the recent ones; the range widens
  // when nothing fits. `energetic` asks for the highest-reaching range
  // (a drop).
  _pick(exclude, energetic) {
    const lib = this._library;
    if (energetic) {
      let best = null;
      for (const p of lib) if (p !== exclude && (!best || p.energy[1] + p.energy[0] > best.energy[1] + best.energy[0])) best = p;
      return best || lib[0];
    }
    const e = this._energy;
    for (const widen of [0, 0.15, 0.4, 1]) {
      const fit = lib.filter((p) => p !== exclude && !this._history.includes(p) && e >= p.energy[0] - widen && e <= p.energy[1] + widen);
      if (fit.length) return fit[Math.floor(Math.random() * fit.length)];
    }
    return lib.find((p) => p !== exclude) || lib[0];
  }

  _remember(p) {
    this._history.unshift(p);
    if (this._history.length > HISTORY) this._history.length = HISTORY;
  }

  // FADE: from the parameters AS APPLIED this frame (the beat's zoom push,
  // the bar's rotation impulse are on top of the stored ones), to the new
  // preset's, over FADE_S. The wave types crossfade by opacity.
  _fadeTo(preset) {
    this._fromPreset = this._toPreset;
    this._from = Object.assign({}, this._cur);
    this._from.waveType = this._to.waveType; this._from.waveColor = this._to.waveColor; this._from.shapes = this._to.shapes; this._from.name = this._to.name;
    this._toPreset = preset;
    this._to = this._params(preset);
    this._t = 0;
    this._cutLast = false;
    this._remember(preset);
  }

  // CUT: one frame, the buffer untouched - the previous image is caught in
  // the new field.
  _cutTo(preset) {
    this._fromPreset = preset;
    this._toPreset = preset;
    this._from = this._params(preset);
    this._to = this._params(preset);
    this._t = 1;
    this._cutLast = true;
    this._remember(preset);
  }

  // --- the wave ------------------------------------------------------------------------
  _makeWave() {
    const n = WAVE_POINTS;
    const pos = new Float32Array(n * 2 * 3), side = new Float32Array(n * 2), along = new Float32Array(n * 2);
    const idx = [];
    for (let i = 0; i < n; i++) {
      side[i * 2] = -1; side[i * 2 + 1] = 1;
      along[i * 2] = along[i * 2 + 1] = i / (n - 1);
      if (i < n - 1) { const a = i * 2; idx.push(a, a + 1, a + 3, a, a + 3, a + 2); }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('aSide', new THREE.BufferAttribute(side, 1));
    geo.setAttribute('aAlong', new THREE.BufferAttribute(along, 1));
    geo.setIndex(idx);
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 10);
    const mat = new THREE.ShaderMaterial({
      uniforms: { uHalf: { value: 0.004 }, uColor: { value: new THREE.Color(0xffffff) }, uOpacity: { value: 1 }, uDots: { value: 0 } },
      vertexShader: waveVertexShader, fragmentShader: waveFragmentShader,
      side: THREE.DoubleSide, blending: THREE.AdditiveBlending, transparent: true, depthTest: false, depthWrite: false
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;
    mesh.visible = false;
    return { mesh, geo, mat, pos };
  }

  // The curve: amplitude per point from the three bands through gaussians,
  // decorrelated, through EqRig's attack/release ballistics, plus a wobble
  // at the tempo. Returns the amplitude in ortho units per point.
  _curve(dt, beatPhase, boost) {
    const ka = 1 - Math.exp(-dt / ATTACK_TAU), kr = 1 - Math.exp(-dt / RELEASE_TAU);
    const b = this._bands, g = this._gauss, lv = this._level;
    const ph = beatPhase != null ? beatPhase : (this._time * (this._bpm || 120) / 60) % 1;
    for (let i = 0; i < WAVE_POINTS; i++) {
      const target = (b[0] * g[0][i] + b[1] * g[1][i] + b[2] * g[2][i]) * this._decor[i];
      lv[i] += (target - lv[i]) * (target > lv[i] ? ka : kr);
    }
    const out = this._amp || (this._amp = new Float32Array(WAVE_POINTS));
    for (let i = 0; i < WAVE_POINTS; i++) {
      const x = i / (WAVE_POINTS - 1);
      const wobble = Math.sin((ph + x * 2) * Math.PI * 2) * TEMPO_WOBBLE;
      out[i] = WAVE_AMP * boost * (lv[i] * (1 + wobble) + 0.08 * wobble);
    }
    return out;
  }

  // Lays the ribbon of `wave` along the curve for a type; normals packed in z.
  _layWave(wave, type, amp, angle, aspect) {
    const n = WAVE_POINTS, pos = wave.pos;
    const ca = Math.cos(angle), sa = Math.sin(angle);
    const xs = this._wx || (this._wx = new Float32Array(n)), ys = this._wy || (this._wy = new Float32Array(n));
    const half = 0.46 * aspect;
    for (let i = 0; i < n; i++) {
      const u = i / (n - 1);
      let x, y;
      if (type === 'CIRCLE') { const th = u * Math.PI * 2, rr = 0.22 + amp[i] * 1.5; x = Math.cos(th) * rr; y = Math.sin(th) * rr; }
      else if (type === 'SPIRO') { const th = u * Math.PI * 2; const a = this._spiroA || 3, b = this._spiroB || 2; x = Math.sin(a * th + this._spiroPhase) * (0.30 * aspect) * (0.8 + amp[i] * 2); y = Math.sin(b * th) * 0.30 * (0.8 + amp[i] * 2); }
      else if (type === 'MIRROR') { const uu = u < 0.5 ? u * 2 : (u - 0.5) * 2; const sgn = u < 0.5 ? 1 : -1; x = (u < 0.5 ? uu : 1 - uu) * 2 * half - half; y = sgn * (0.14 + amp[i] * 1.2); }
      else { x = u * 2 * half - half; y = amp[i] * 2 - WAVE_AMP * 0.5; }
      xs[i] = x * ca - y * sa; ys[i] = x * sa + y * ca;
    }
    for (let i = 0; i < n; i++) {
      const j = Math.min(n - 1, i + 1), k = Math.max(0, i - 1);
      const tx = xs[j] - xs[k], ty = ys[j] - ys[k];
      const nAng = Math.atan2(tx, -ty);   // the normal's angle
      for (let s = 0; s < 2; s++) { const o = (i * 2 + s) * 3; pos[o] = xs[i]; pos[o + 1] = ys[i]; pos[o + 2] = nAng; }
    }
    wave.geo.attributes.position.needsUpdate = true;
  }

  // --- governor (echo.js) ----------------------------------------------------------------
  _measureLuminance(renderer) {
    this._lumUniforms.uBuffer.value = this._buffer.write.texture;
    renderer.setRenderTarget(this._lumTarget);
    renderer.render(this._lumScene, this._ortho);
    renderer.readRenderTargetPixels(this._lumTarget, 0, 0, LUM_SIZE, LUM_SIZE, this._lumPixels);
    let sum = 0;
    for (let i = 0; i < this._lumPixels.length; i += 4) sum += 0.299 * this._lumPixels[i] + 0.587 * this._lumPixels[i + 1] + 0.114 * this._lumPixels[i + 2];
    const lum = sum / (255 * LUM_SIZE * LUM_SIZE);
    this._lastLum = lum;
    if (lum > GOVERN_HIGH) this._governorHot = true;
    if (this._governorHot) {
      if (lum > GOVERN_RESUME) this._governorAdj = Math.min(0.12, this._governorAdj + GOVERN_STEP);
      else this._governorHot = false;
    } else if (lum < GOVERN_LOW && this._governorAdj > 0) {
      this._governorAdj = Math.max(0, this._governorAdj - GOVERN_STEP);
    }
  }

  _runPasses(renderer) {
    const rebuilt = this._buffer.ensureSize(renderer);
    if (rebuilt) {
      this._displayUniforms.uBuffer.value = this._buffer.read.texture;
      const aspect = this._buffer.width / Math.max(1, this._buffer.height);
      this._aspect = aspect;
      this._ortho.left = -0.5 * aspect; this._ortho.right = 0.5 * aspect;
      this._ortho.updateProjectionMatrix();
      this._fb.uAspect.value = aspect;
      const px = 1 / Math.max(1, this._buffer.height);
      for (const w of this._waves) w.mat.uniforms.uHalf.value = WAVE_PX * WAVE_HALO * 0.5 * px;
    }
    if (this._paused) { this._displayUniforms.uBuffer.value = this._buffer.read.texture; return; }
    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    this._fb.uPrevious.value = this._buffer.read.texture;
    this._fb.uDecay.value = this._fading ? FADE_DECAY : this._fb.uDecay.value;
    this._fb.uSeed.value = (this._frameCount * 13.13) % 64;
    renderer.setRenderTarget(this._buffer.write);
    renderer.autoClear = false;
    renderer.render(this._feedbackScene, this._ortho);
    renderer.render(this._injectScene, this._ortho);
    this._frameCount++;
    if (!this._fading && this._frameCount % GOVERN_EVERY_FRAMES === 0) this._measureLuminance(renderer);
    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAutoClear;
    this._buffer.swap();
    this._displayUniforms.uBuffer.value = this._buffer.read.texture;
    if (this._listEl && this._frameCount % 10 === 0) {
      this._listEl.textContent = 'preset  ' + this._fromPreset.name + ' -> ' + this._toPreset.name
        + '\n        t ' + this._t.toFixed(2) + (this._cutLast ? ' (cut)' : ' (fade)')
        + '\n        energy ' + this._energy.toFixed(2) + '  lum ' + this._lastLum.toFixed(3) + '  decay ' + this._fb.uDecay.value.toFixed(3)
        + (this._governorHot ? '\n        GOVERNOR ACTIVE' : '');
    }
  }

  _applyPalette(palette) {
    const vivid = (palette && palette.vividColors && palette.vividColors.length) ? palette.vividColors : [0x39a0ff, 0xff3ba7, 0xffd23b];
    for (let i = 0; i < 3; i++) this._palette[i].set(vivid[i % vivid.length]);
  }

  // --- the frame -------------------------------------------------------------------------
  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    const director = profile.director;
    const bands = director && director.hasReliableGrid && director.bands;
    const k = 1 - Math.exp(-dt / BAND_TAU);
    this._lowSmooth += ((bands ? clamp01(bands.low) : 0.5) - this._lowSmooth) * k;
    this._midSmooth += ((bands ? clamp01(bands.mid) : 0.5) - this._midSmooth) * k;
    this._highSmooth += ((bands ? clamp01(bands.high) : 0.5) - this._highSmooth) * k;
    this._bands[0] = this._lowSmooth; this._bands[1] = this._midSmooth; this._bands[2] = this._highSmooth;
    this._bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || null;
    // the segment's energy: the timeline's, else the director's envelope
    const tl = profile.analysisTimeline;
    const nowMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    const e = tl && nowMs != null ? tl.lookupEnergy(nowMs) : null;
    this._energy = e != null ? e / 100 : (director && director.intensity != null ? director.intensity : 0.6);

    if (profile.artworkPalette !== this._lastPaletteRef) { this._lastPaletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    if (profile.trackId !== this._lastTrackId) {
      const first = this._lastTrackId === undefined;
      this._lastTrackId = profile.trackId;
      if (!first) this._fadeUntil = this._time + TRACK_FADE_S;
      this._firedDrops.clear();
    }
    this._fading = this._time < this._fadeUntil;
    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) {
        const self = this;
        loadArtworkTexture(profile.artworkUrl, { wrap: THREE.ClampToEdgeWrapping, mipmaps: false }, function (tex) {
          if (self._artTexture) self._artTexture.dispose();
          self._artTexture = tex;
          for (const s of self._shapes) { s.material.map = tex; s.material.needsUpdate = true; }
        }, function () {});
      }
    }

    // --- the events ---
    const beatIndex = profile.beatIndex, barIndex = profile.barIndex, phraseIndex = profile.phraseIndex;
    const newBeat = beatIndex != null && this._lastBeatIndex != null && beatIndex !== this._lastBeatIndex;
    const newBar = barIndex != null && this._lastBarIndex != null && barIndex !== this._lastBarIndex;
    const newPhrase = phraseIndex != null && this._lastPhraseIndex != null && phraseIndex !== this._lastPhraseIndex;
    this._lastBeatIndex = beatIndex; this._lastBarIndex = barIndex; this._lastPhraseIndex = phraseIndex;
    if (newBeat && !this._fading) this._beatUntil = this._time + BEAT_HOLD_S;
    if (newBar && !this._fading) { this._barRot = BAR_ROT; this._hueOffset += BAR_HUE_STEP * Math.PI * 2; }
    // the drop: when the next event, a drop, is upon us (once per drop)
    const ev = director && director.nextEvent;
    let drop = false;
    if (ev && ev.type === 'drop' && ev.msUntil <= 40 && nowMs != null) {
      const key = Math.round((nowMs + ev.msUntil) / 100);
      if (!this._firedDrops.has(key)) { this._firedDrops.add(key); drop = true; }
    }
    const free = !this._forced && !this._blend && !this._fading;
    if (free && drop) {
      this._cutTo(this._pick(this._toPreset, true));
      this._invertFrames = DROP_INVERT_FRAMES;
    } else if (free && newPhrase) {
      this._phraseCount++;
      const next = this._pick(this._toPreset);
      if (this._phraseCount % CUT_EVERY_PHRASES === 0) this._cutTo(next); else this._fadeTo(next);
    }

    // --- the parameters: interpolated, smooth, angular by the short way ---
    if (!this._blend && this._t < 1) this._t = Math.min(1, this._t + dt / FADE_S);
    const t = this._blend ? this._blend.t : smooth(this._t);
    const cur = this._cur;
    for (const key of PARAMS) cur[key] = lerp(this._from[key], this._to[key], t);
    cur.waveAngle = lerpAngle(this._from.waveAngle, this._to.waveAngle, t, ANGULAR.waveAngle);
    // the rhythm, on top
    let zoom = cur.zoom;
    if (this._time < this._beatUntil) zoom += BEAT_ZOOM * this._lowSmooth;
    this._barRot *= Math.exp(-dt / BAR_ROT_TAU);
    const rot = cur.rot + this._barRot;
    this._displayUniforms.uInvert.value = cur.invert;
    this._displayUniforms.uFlash.value = this._invertFrames > 0 ? 1 : 0;
    if (this._invertFrames > 0) this._invertFrames--;
    this._hueAngle += cur.hueShift * Math.PI * 2;
    hueMatrix(this._hueAngle + this._hueOffset, this._fb.uHueMat.value);
    this._hueAngle %= Math.PI * 2;
    const decay = this._forcedDecay != null ? this._forcedDecay : Math.max(DECAY_MIN, Math.min(DECAY_MAX, cur.decay - this._governorAdj));

    const fb = this._fb;
    fb.uCentre.value.set(cur.cx, cur.cy);
    fb.uZoom.value = zoom; fb.uZoomExp.value = cur.zoomexp;
    fb.uRot.value = rot; fb.uRotR.value = cur.rotr;
    fb.uDelta.value.set(cur.dx, cur.dy);
    fb.uStretch.value.set(cur.sx, cur.sy);
    fb.uWarp.value = this._layout.warp ? cur.warp : 0; fb.uWarpScale.value = Math.max(0.2, cur.warpScale); fb.uWarpSpeed.value = cur.warpSpeed;
    fb.uDecay.value = decay; fb.uDarken.value = cur.darkenCenter;
    fb.uTime.value = this._time % 1000;

    // --- the waves: the curve once, laid for the two types, crossfaded ---
    const aspect = this._aspect || 16 / 9;
    const boost = this._time < this._beatUntil ? 2 : 1;
    const amp = this._curve(dt, profile.beatPhase, boost);
    // SPIRO: integer ratios, the figure turning once per 8 beats
    this._spiroA = 3; this._spiroB = 2;
    this._spiroPhase += dt * Math.PI * 2 * ((this._bpm || 120) / 60) / 8;
    const types = [this._from.waveType, this._to.waveType];
    const opac = [1 - t, t];
    if (types[0] === types[1]) { opac[0] = 0; opac[1] = 1; }
    for (let i = 0; i < 2; i++) {
      const w = this._waves[i];
      const show = opac[i] > 0.01 && !this._fading;
      w.mesh.visible = show;
      if (!show) continue;
      this._layWave(w, types[i], amp, cur.waveAngle * Math.PI * 2, aspect);
      const colorIdx = i === 0 ? this._from.waveColor : this._to.waveColor;
      w.mat.uniforms.uColor.value.copy(this._palette[colorIdx % 3]);
      w.mat.uniforms.uOpacity.value = WAVE_GAIN * opac[i];
      w.mat.uniforms.uDots.value = types[i] === 'DOTS' ? 1 : 0;
    }

    // --- the shapes: the cover on polygons, turning and pulsing ---
    const count = Math.round(lerp(this._from.shapes, this._to.shapes, t));
    const pulse = 1 + SHAPE_PULSE * (profile.beatPhase != null ? Math.pow(1 - profile.beatPhase, 3) : 0) + 0.1 * this._lowSmooth;
    for (let i = 0; i < 3; i++) {
      const s = this._shapes[i];
      s.visible = i < count && !!this._artTexture && !this._fading;
      if (!s.visible) continue;
      const spin = this._time * (0.25 + i * 0.12) * (i % 2 ? -1 : 1) + i * 2.1;
      const orbit = count > 1 ? 0.22 : 0;
      s.position.set(Math.cos(spin * 0.5 + i * 2.09) * orbit * aspect, Math.sin(spin * 0.5 + i * 2.09) * orbit, 0);
      s.rotation.z = spin;
      const size = SHAPE_SIZE * pulse * (1 - i * 0.15);
      s.scale.set(size, size, 1);
    }
  }

  get probe() {
    const cur = this._cur || {};
    return {
      from: this._fromPreset && this._fromPreset.name, to: this._toPreset && this._toPreset.name, t: +this._t.toFixed(3), cut: this._cutLast,
      energy: +this._energy.toFixed(2), lum: +this._lastLum.toFixed(3), decay: +this._fb.uDecay.value.toFixed(3), governorAdj: this._governorAdj,
      zoom: +this._fb.uZoom.value.toFixed(4), rot: +this._fb.uRot.value.toFixed(4), rotr: +this._fb.uRotR.value.toFixed(4), warp: +this._fb.uWarp.value.toFixed(4),
      invert: this._displayUniforms.uInvert.value, flash: this._displayUniforms.uFlash.value, waveType: this._to && this._to.waveType, shapes: this._to && this._to.shapes, library: this._library.length, frames: this._frameCount
    };
  }

  dispose() {
    this._scene.remove(this._displayMesh);
    this._displayMesh.geometry.dispose(); this._displayMesh.material.dispose();
    this._feedbackQuad.geometry.dispose(); this._feedbackQuad.material.dispose();
    for (const w of this._waves) { w.geo.dispose(); w.mat.dispose(); }
    for (const s of this._shapes) { s.geometry.dispose(); s.material.dispose(); }
    this._lumQuad.geometry.dispose(); this._lumQuad.material.dispose();
    this._lumTarget.dispose();
    this._buffer.dispose();
    if (this._artTexture) this._artTexture.dispose();
    if (this._listEl && this._listEl.parentNode) this._listEl.parentNode.removeChild(this._listEl);
  }
}
